import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, InputAdornment, IconButton as MuiIconButton,
  Dialog, DialogContent, DialogActions, DialogTitle
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import ShieldIcon from '@mui/icons-material/Shield';
import illustration from '../assets/images/illustration.png';
import { useMutation, gql, useApolloClient } from '@apollo/client';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import AuthContext from '../contexts/AuthContext';

// GraphQL mutations
const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      success
      requiresOtp
      message
      user {
        id
        email
        firstName
        lastName
        role
      }
      token
    }
  }
`;

const REQUEST_PASSWORD_RESET_MUTATION = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email) {
      success
      message
    }
  }
`;

const VERIFY_OTP_MUTATION = gql`
  mutation VerifyOTP($email: String!, $otp: String!) {
    verifyOtp(email: $email, otp: $otp) {
      success
      message
      user {
        id
        email
        firstName
        lastName
        role
      }
      token
    }
  }
`;

// Validation schemas
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Email invalide')
    .required('Email requis'),
  password: Yup.string()
    .required('Mot de passe requis')
});

const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .required('Code OTP requis')
    .matches(/^\d{6}$/, 'Le code OTP doit contenir 6 chiffres')
});

function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useContext(AuthContext);
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const client = useApolloClient();

  // État pour la réinitialisation du mot de passe
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');

  // Rediriger si déjà authentifié
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Réinitialiser l'état du formulaire et le cache lors du montage du composant
  useEffect(() => {
    // Réinitialiser l'état local
    setShowOtpForm(false);
    setErrorMessage('');
    setUserEmail('');
    
    // Réinitialiser le cache Apollo pour les requêtes d'authentification
    client.cache.evict({ fieldName: 'login' });
    client.cache.evict({ fieldName: 'verifyOtp' });
    client.cache.evict({ fieldName: 'me' });
    client.cache.gc();
    
    // Forcer un rechargement des requêtes
    client.resetStore();
  }, [client]);

  // Login mutation
  const [loginMutation, { loading: loginLoading }] = useMutation(LOGIN_MUTATION, {
    onCompleted: (data) => {
      if (data.login.success) {
        if (data.login.requiresOtp) {
          // Show OTP form if OTP is required
          setShowOtpForm(true);
          setErrorMessage('');
        } else {
          // Login directly if OTP is not required
          console.log("Token received:", data.login.token); // Debug log
          login(data.login.token, () => {
            navigate('/');
          });
        }
      } else {
        setErrorMessage(data.login.message);
      }
    },
    onError: (error) => {
      console.error("Login error:", error); // Debug log
      setErrorMessage('Erreur de connexion: ' + error.message);
    }
  });

  // Verify OTP mutation
  const [verifyOtpMutation, { loading: otpLoading }] = useMutation(VERIFY_OTP_MUTATION, {
    onCompleted: (data) => {
      if (data.verifyOtp.success) {
        login(data.verifyOtp.token, () => {
          navigate('/');
        });
      } else {
        setErrorMessage(data.verifyOtp.message);
      }
    },
    onError: (error) => {
      setErrorMessage('Erreur de vérification OTP: ' + error.message);
    }
  });

  // Mutation de demande de réinitialisation du mot de passe
  const [requestPasswordReset, { loading: resetLoading }] = useMutation(REQUEST_PASSWORD_RESET_MUTATION, {
    onCompleted: (data) => {
      if (data.requestPasswordReset.success) {
        setResetMessage(data.requestPasswordReset.message);
        setResetError('');
      } else {
        setResetError(data.requestPasswordReset.message);
        setResetMessage('');
      }
    },
    onError: (error) => {
      setResetError('Erreur: ' + error.message);
      setResetMessage('');
    }
  });

  const handleOpenResetDialog = () => {
    setResetEmail('');
    setResetMessage('');
    setResetError('');
    setOpenResetDialog(true);
  };

  const handleRequestReset = () => {
    if (!resetEmail || !/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetError('Veuillez saisir une adresse email valide');
      return;
    }
    requestPasswordReset({ variables: { email: resetEmail } });
  };

  // Handle login form submission
  const handleLogin = (values) => {
    setUserEmail(values.email);
    loginMutation({
      variables: {
        email: values.email,
        password: values.password
      }
    });
  };

  // Handle OTP form submission
  const handleOtpVerification = (values) => {
    verifyOtpMutation({
      variables: {
        email: userEmail,
        otp: values.otp
      }
    });
  };

  return (
    <Container component="main" maxWidth="xl">
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          gap: { md: 8, lg: 12 },
          pt: 2,
          pb: 4,
        }}
      >
        {/* Colonne gauche — illustration */}
        <Box
          sx={{
            flex: '0 0 auto',
            maxWidth: { md: 380, lg: 420 },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 900, color: '#006d78', textAlign: 'center', mb: 1, lineHeight: 1.2 }}>
            Bienvenue sur <Box component="span" sx={{ color: '#d21433' }}>GEVAA</Box>
          </Typography>
          <Typography variant="body1" sx={{ color: '#555', textAlign: 'center', mb: 2, maxWidth: 440, lineHeight: 1.6, fontSize: '0.95rem' }}>
            Connectez-vous pour accéder à votre espace de gestion des visites et des absences administratives.
          </Typography>

          <Box
            component="img"
            src={illustration}
            alt="Illustration GEVAA"
            sx={{
              maxWidth: '100%',
              maxHeight: { md: 320, lg: 380 },
              objectFit: 'contain',
              filter: 'drop-shadow(0 8px 24px rgba(0,116,129,0.18))',
            }}
          />
        </Box>

        {/* Colonne droite — formulaire */}
        <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 480, lg: 520 }, ml: { md: -4 }, mt: { md: 3 } }}>
          {/* Titre mobile uniquement */}
          <Typography component="h2" variant="h5" align="center" gutterBottom sx={{ display: { md: 'none' }, fontWeight: 800, color: '#006d78', mb: 3 }}>
            Bienvenue sur <Box component="span" sx={{ color: '#d21433' }}>GEVAA</Box>
          </Typography>

          <Paper elevation={4} sx={{
            p: { xs: 4, md: 5 }, width: '100%', borderRadius: 4,
            border: '1px solid rgba(0,116,129,0.12)',
            boxShadow: '0 8px 40px rgba(0,116,129,0.10)',
          }}>
            {/* En-tête formulaire */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{
                width: 70, height: 70, borderRadius: '50%', mx: 'auto', mb: 2,
                background: showOtpForm
                  ? 'linear-gradient(135deg, #ffc71f, #e6aa00)'
                  : 'linear-gradient(135deg, #006d78, #005a63)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {showOtpForm
                  ? <ShieldIcon sx={{ color: '#fff', fontSize: 36 }} />
                  : <LoginIcon sx={{ color: '#fff', fontSize: 36 }} />}
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a' }}>
                {showOtpForm ? 'Vérification à deux facteurs' : 'Connexion'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', mt: 0.5 }}>
                {showOtpForm
                  ? `Code envoyé à ${userEmail}`
                  : 'Entrez vos identifiants pour continuer'}
              </Typography>
              {!showOtpForm && (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.8,
                  background: 'linear-gradient(135deg, #006d78, #005a63)',
                  color: '#fff', borderRadius: 5, px: 2, py: 0.6, mt: 1.5,
                  fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                }}>
                  <ShieldIcon sx={{ fontSize: 14 }} /> Accès sécurisé
                </Box>
              )}
            </Box>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box>
            {!showOtpForm ? (
              <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={loginSchema}
                onSubmit={handleLogin}
              >
                {({ errors, touched, handleChange, handleBlur }) => (
                  <Form>
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Adresse email"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: '#006d78', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Mot de passe"
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      autoComplete="current-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: '#006d78', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <MuiIconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </MuiIconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={loginLoading ? null : <LoginIcon />}
                      sx={{
                        mt: 3, mb: 1, py: 1.4, borderRadius: 2, fontWeight: 700,
                        background: 'linear-gradient(135deg, #006d78, #005a63)',
                        '&:hover': { background: 'linear-gradient(135deg, #005a63, #003d45)' },
                      }}
                      disabled={loginLoading}
                    >
                      {loginLoading ? <CircularProgress size={24} color="inherit" /> : 'Se connecter'}
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleOpenResetDialog}
                        sx={{ color: '#006d78', textTransform: 'none', fontWeight: 600 }}
                      >
                        Mot de passe oublié ?
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            ) : (
              <Formik
                initialValues={{ otp: '' }}
                validationSchema={otpSchema}
                onSubmit={handleOtpVerification}
              >
                {({ errors, touched, handleChange, handleBlur }) => (
                  <Form>
                    <Field
                      as={TextField}
                      margin="normal"
                      required
                      fullWidth
                      id="otp"
                      label="Code à 6 chiffres"
                      name="otp"
                      autoFocus
                      inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.otp && Boolean(errors.otp)}
                      helperText={touched.otp && errors.otp}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShieldIcon sx={{ color: '#ffc71f', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      sx={{
                        mt: 3, mb: 1, py: 1.4, borderRadius: 2, fontWeight: 700,
                        background: 'linear-gradient(135deg, #ffc71f, #e6aa00)',
                        color: '#3d2000',
                        '&:hover': { background: 'linear-gradient(135deg, #e6aa00, #c98e00)' },
                      }}
                      disabled={otpLoading}
                    >
                      {otpLoading ? <CircularProgress size={24} color="inherit" /> : 'Vérifier le code'}
                    </Button>
                    <Button
                      fullWidth
                      variant="text"
                      onClick={() => setShowOtpForm(false)}
                      sx={{ color: '#006d78' }}
                    >
                      ← Retour à la connexion
                    </Button>
                  </Form>
                )}
              </Formik>
            )}
          </Box>
        </Paper>
        </Box>
      </Box>

      {/* Dialogue de réinitialisation du mot de passe */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#006d78' }}>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            Saisissez l'adresse email de votre compte. Si un compte existe, vous recevrez un lien
            pour définir un nouveau mot de passe.
          </Typography>
          {resetMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>{resetMessage}</Alert>
          )}
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>
          )}
          <TextField
            fullWidth
            autoFocus
            label="Adresse email"
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#006d78', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenResetDialog(false)}>Fermer</Button>
          <Button
            onClick={handleRequestReset}
            variant="contained"
            disabled={resetLoading || Boolean(resetMessage)}
            sx={{
              background: 'linear-gradient(135deg, #006d78, #005a63)',
              '&:hover': { background: 'linear-gradient(135deg, #005a63, #003d45)' },
            }}
          >
            {resetLoading ? <CircularProgress size={22} color="inherit" /> : 'Envoyer le lien'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Login; 